/** Привязки мест к категориям.
 *
 * Пины работают невидимо: поправил категорию у траты — бот запомнил место и
 * дальше ставит её сам, ничего не сообщая. Пока он прав, это удобно; в момент,
 * когда упорно ставит не ту, сделать было нечего. Здесь можно переучить.
 */
import { useEffect, useState } from "react";
import { ApiError, api, type Failure, type Pin } from "../lib/api.ts";
import { plural } from "../lib/plural.ts";
import { rowButton } from "../lib/rowStyle.ts";
import { tap } from "../lib/telegram.ts";
import { Header } from "../components/Header.tsx";
import { Fallback } from "./Fallback.tsx";
import { card, hint } from "../components/ui.tsx";

export function PinsScreen({ onBack }: { onBack: () => void }) {
  const [rows, setRows] = useState<Pin[] | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [busy, setBusy] = useState("");
  const [trouble, setTrouble] = useState("");

  const load = () => {
    setFailure(null);
    api.pins().then(setRows)
      .catch((e) => setFailure(e instanceof ApiError ? e.kind : "broken"));
  };

  useEffect(load, []);

  if (failure) return <Fallback kind={failure} onRetry={load} />;

  return (
    <div style={{ padding: "var(--screen)" }}>
      <Header title="Мои привязки" onBack={onBack} />

      <p style={{ ...hint, marginTop: 0 }}>
        Когда ты поправил категорию у траты, бот запомнил это место и дальше ставит её сам.
        Здесь видно, что именно он запомнил.
      </p>

      {rows === null ? <Skeleton /> : rows.length === 0 ? (
        <p style={{ ...hint, textAlign: "center", marginTop: "var(--s-6)" }}>
          Пока ничего не запомнил — поправь категорию у любой траты, и место появится здесь.
        </p>
      ) : (
        <>
          <section style={{ ...card, marginTop: "var(--s-4)", padding: "2px var(--s-5)" }}>
            {rows.map((pin, i) => (
              <div key={pin.place} style={{ ...rowButton(i === rows.length - 1),
                                            cursor: "default" }}>
                <span style={{ flexGrow: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: "var(--f-body)" }}>{pin.place}</span>
                  <span style={{ display: "block", fontSize: "var(--f-caption)",
                                 color: "var(--text-muted)", marginTop: 2 }}>
                    → {pin.category_name}
                  </span>
                </span>
                <button type="button" aria-disabled={busy === pin.place || undefined}
                        onClick={() => {
                          if (busy === pin.place) return;
                          tap();
                          setTrouble("");
                          setBusy(pin.place);
                          api.forgetPin(pin.place).then(load).catch(() => setTrouble("Не получилось забыть место. Попробуй ещё раз."))
                            .finally(() => setBusy(""));
                        }}
                        style={{ minHeight: 38, padding: "8px 12px", borderRadius: "var(--r-sm)",
                                 border: "none", cursor: "pointer", font: "inherit",
                                 fontSize: "var(--f-caption)", background: "var(--surface-2)",
                                 color: "var(--text)" }}>
                  Забыть
                </button>
              </div>
            ))}
          </section>
          {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}
          <p style={hint}>
            Забыл {plural(rows.length, ["место", "места", "мест"])} — и бот снова будет
            решать сам, как в первый раз.
          </p>
        </>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    <div style={{ ...card, marginTop: "var(--s-4)" }} role="status" aria-busy="true" aria-label="Загружаю привязки">
      <div style={{ height: 11, width: "50%", background: "var(--surface-3)", borderRadius: 5 }} />
    </div>
  );
}

